import { objectSpread2 as _objectSpread2 } from '../_virtual/_rollupPluginBabelHelpers.mjs';
import { extractDomain, DEVELOPMENT_DOMAIN, PRODUCE_BATCH_ENDPOINT, PRODUCE_ENDPOINT, PRODUCTION_DOMAIN } from '../config/endpoints.mjs';
import { convertAllEventsToUnderscoreCase, toUnderscoreCase } from '../helpers/convert-case.mjs';
import { v4 } from '../helpers/uuid.mjs';
import { MonorailRequestError, MonorailBatchProduceError, MonorailUnableToProduceError } from './producer-errors.mjs';

class HttpProducer {
  static withEndpoint(edgeEndpoint) {
    return new HttpProducer(extractDomain(edgeEndpoint));
  }
  constructor(edgeDomain = DEVELOPMENT_DOMAIN, keepalive = false) {
    this.edgeDomain = edgeDomain;
    this.keepalive = keepalive;
  }
  async produceBatch(monorailEvent) {
    const body = {
      events: convertAllEventsToUnderscoreCase(monorailEvent),
      metadata: toUnderscoreCase(monorailEvent.metadata)
    };
    let edgeResponse;
    try {
      edgeResponse = await fetch(this.produceBatchEndpoint(), {
        method: 'post',
        headers: getHeadersFromMetadata(monorailEvent.metadata),
        body: JSON.stringify(body),
        keepalive: this.keepalive
      });
    } catch (error) {
      throw new MonorailRequestError(error, monorailEvent.events.map(event => event.schemaId));
    }
    if (edgeResponse.status === 207) {
      const response = await edgeResponse.json();
      throw new MonorailBatchProduceError(response);
    }
    if (!edgeResponse.ok) {
      throw new MonorailUnableToProduceError({
        status: edgeResponse.status,
        message: await edgeResponse.text()
      });
    }
    return {
      status: edgeResponse.status
    };
  }
  async produce(monorailEvent) {
    let convertToUnderscoreCase = true;
    if (monorailEvent && monorailEvent.options && Object.prototype.hasOwnProperty.call(monorailEvent.options, 'convertEventCase')) {
      convertToUnderscoreCase = Boolean(monorailEvent.options.convertEventCase);
    }
    let edgeResponse;
    try {
      edgeResponse = await produceHttpEvent({
        endpoint: this.produceEndpoint(),
        keepalive: this.keepalive,
        event: _objectSpread2(_objectSpread2({}, monorailEvent), {}, {
          payload: toUnderscoreCase(monorailEvent.payload, convertToUnderscoreCase)
        })
      });
    } catch (error) {
      throw new MonorailRequestError(error, [monorailEvent.schemaId]);
    }
    if (!edgeResponse) {
      throw new MonorailUnableToProduceError({
        message: 'No response from edge'
      });
    }
    if (!edgeResponse.ok) {
      throw new MonorailUnableToProduceError({
        status: edgeResponse.status,
        message: await edgeResponse.text()
      });
    }
    return {
      status: edgeResponse.status
    };
  }
  produceBatchEndpoint() {
    return this.edgeDomain + PRODUCE_BATCH_ENDPOINT;
  }
  produceEndpoint() {
    return this.edgeDomain + PRODUCE_ENDPOINT;
  }
}
function getHeadersFromMetadata(metadata) {
  /* eslint-disable @typescript-eslint/naming-convention */
  const headers = {
    'Content-Type': 'application/json; charset=utf-8',
    'X-Monorail-Edge-Event-Created-At-Ms': (metadata && metadata.eventCreatedAtMs || Date.now()).toString(),
    'X-Monorail-Edge-Event-Sent-At-Ms': Date.now().toString(),
    'X-Monorail-Edge-Client-Message-Id': (metadata && metadata.clientMessageId || v4()).toString()
  };
  /* eslint-enable @typescript-eslint/naming-convention */

  if (metadata && metadata.userAgent) {
    headers['User-Agent'] = metadata.userAgent;
  }
  if (metadata && metadata.remoteIp) {
    headers['X-Forwarded-For'] = metadata.remoteIp;
  }
  if (metadata && metadata.deviceInstallId) {
    headers['X-Monorail-Edge-Device-Install-Id'] = metadata.deviceInstallId;
  }
  if (metadata && metadata.client) {
    headers['X-Monorail-Edge-Client'] = metadata.client;
  }
  if (metadata && metadata.clientOs) {
    headers['X-Monorail-Edge-Client-OS'] = metadata.clientOs;
  }
  return headers;
}

// Allows avoiding HttpProducer entirely by passing in snake-cased events (but we still get typechecking)
async function produceHttpEvent({
  endpoint,
  event,
  keepalive
}) {
  return fetch(endpoint !== null && endpoint !== undefined ? endpoint : PRODUCTION_DOMAIN + PRODUCE_ENDPOINT, {
    method: 'post',
    headers: getHeadersFromMetadata(event.metadata),
    /* eslint-disable @typescript-eslint/naming-convention */
    body: JSON.stringify(_objectSpread2(_objectSpread2({
      schema_id: event.schemaId,
      payload: event.payload
    }, event.metadata && {
      metadata: {
        client_message_id: event.metadata.clientMessageId,
        event_created_at_ms: event.metadata.eventCreatedAtMs
      }
    }), event.consent && {
      consent: event.consent,
      consent_provider: event.consent_provider,
      consent_version: event.consent_version
    })),
    /* eslint-enable @typescript-eslint/naming-convention */
    keepalive
  });
}

export { HttpProducer, produceHttpEvent };
